<script setup>
import { ref, nextTick, watch, onMounted } from 'vue';
import ContactAPI from 'dashboard/api/contacts';

const props = defineProps({
  card: { type: Object, required: true },
});

const emit = defineEmits(['updated', 'editing']);

const SOCIAL_LINKS = [
  {
    key: 'website',
    label: 'Site',
    svg: '<svg xmlns="http://www.w3.org/2000/svg" style="width:16px;height:16px;" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>',
  },
  {
    key: 'instagram',
    label: 'Instagram',
    svg: '<svg xmlns="http://www.w3.org/2000/svg" style="width:16px;height:16px;" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>',
  },
  {
    key: 'facebook',
    label: 'Facebook',
    svg: '<svg xmlns="http://www.w3.org/2000/svg" style="width:16px;height:16px;" width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg>',
  },
  {
    key: 'linkedin',
    label: 'LinkedIn',
    svg: '<svg xmlns="http://www.w3.org/2000/svg" style="width:16px;height:16px;" width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/><rect x="2" y="9" width="4" height="12"/><circle cx="4" cy="4" r="2"/></svg>',
  },
  {
    key: 'youtube',
    label: 'YouTube',
    svg: '<svg xmlns="http://www.w3.org/2000/svg" style="width:16px;height:16px;" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22.54 6.42a2.78 2.78 0 0 0-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46A2.78 2.78 0 0 0 1.46 6.42 29 29 0 0 0 1 12a29 29 0 0 0 .46 5.58a2.78 2.78 0 0 0 1.95 1.96C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 0 0 1.95-1.96A29 29 0 0 0 23 12a29 29 0 0 0-.46-5.58z"/><polygon points="9.75 15.02 15.5 12 9.75 8.98 9.75 15.02" fill="currentColor" stroke="none"/></svg>',
  },
  {
    key: 'tiktok',
    label: 'TikTok',
    svg: '<svg xmlns="http://www.w3.org/2000/svg" style="width:16px;height:16px;" width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.18 8.18 0 0 0 4.78 1.52V6.76a4.85 4.85 0 0 1-1.01-.07z"/></svg>',
  },
  {
    key: 'twitter',
    label: 'Twitter/X',
    svg: '<svg xmlns="http://www.w3.org/2000/svg" style="width:16px;height:16px;" width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.73-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>',
  },
  {
    key: 'google',
    label: 'Google Meu Negócio',
    svg: '<svg xmlns="http://www.w3.org/2000/svg" style="width:16px;height:16px;" width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>',
  },
];

// Estado local — fonte verdade para reatividade imediata
const localProfiles = ref(
  props.card.contact?.additional_attributes?.social_profiles ?? {}
);

// Ao montar, busca o contato completo para carregar os social_profiles persistidos
onMounted(async () => {
  const contactId = props.card.contact?.id;
  if (!contactId) return;
  try {
    const { data } = await ContactAPI.show(contactId);
    const profiles = (data.payload ?? data).additional_attributes
      ?.social_profiles;
    if (profiles) localProfiles.value = profiles;
  } catch {
    // falha silenciosa — ícones ficam apagados até o usuário adicionar
  }
});

// Sincroniza se o card vier com additional_attributes populado
watch(
  () => props.card.contact?.additional_attributes?.social_profiles,
  val => {
    if (val) localProfiles.value = val;
  }
);

function getLink(key) {
  return localProfiles.value[key] || '';
}

const editingKey = ref(null);
const draft = ref('');
const inputRef = ref(null);
const committing = ref(false);

function openEdit(key, e) {
  e.stopPropagation();
  editingKey.value = key;
  draft.value = getLink(key);
  committing.value = false;
  emit('editing', true);
  nextTick(() => {
    inputRef.value?.focus();
    inputRef.value?.select();
  });
}

function cancelEdit() {
  committing.value = true;
  editingKey.value = null;
  draft.value = '';
  emit('editing', false);
}

async function saveLink() {
  if (committing.value) return;
  committing.value = true;

  const key = editingKey.value;
  const value = draft.value.trim();
  editingKey.value = null;
  draft.value = '';
  emit('editing', false);

  const contactId = props.card.contact?.id;
  if (!contactId) return;

  const updated = { ...localProfiles.value, [key]: value };

  const existing = props.card.contact?.additional_attributes ?? {};
  await ContactAPI.update(contactId, {
    additional_attributes: { ...existing, social_profiles: updated },
  });

  // Atualiza estado local imediatamente — não depende do serializer do store
  localProfiles.value = updated;

  committing.value = false;
  emit('updated');
}

function onKeydown(e) {
  if (e.key === 'Enter') {
    e.preventDefault();
    saveLink();
  }
  if (e.key === 'Escape') cancelEdit();
}

function handleClick(key, e) {
  const link = getLink(key);
  if (link) {
    window.open(link, '_blank', 'noopener,noreferrer');
  } else {
    openEdit(key, e);
  }
}

function handleDblClick(key, e) {
  e.preventDefault();
  openEdit(key, e);
}

function handleContextMenu(key, e) {
  e.preventDefault();
  openEdit(key, e);
}
</script>

<template>
  <div class="relative">
    <div class="flex items-center gap-2">
      <div v-for="item in SOCIAL_LINKS" :key="item.key">
        <button
          v-tooltip="
            getLink(item.key)
              ? `${item.label}: ${getLink(item.key)}`
              : `Adicionar ${item.label}`
          "
          class="flex items-center justify-center p-0 bg-transparent border-0 transition-colors"
          :class="
            getLink(item.key)
              ? 'text-n-slate-12 opacity-70'
              : 'text-n-slate-9 opacity-40'
          "
          @click="handleClick(item.key, $event)"
          @dblclick="handleDblClick(item.key, $event)"
          @contextmenu="handleContextMenu(item.key, $event)"
        >
          <span
            style="display: block; width: 16px; height: 16px; flex-shrink: 0"
            v-html="item.svg"
          />
        </button>
      </div>
    </div>
    <input
      v-if="editingKey"
      ref="inputRef"
      v-model="draft"
      type="url"
      :placeholder="`Link do ${SOCIAL_LINKS.find(s => s.key === editingKey)?.label ?? 'link'}...`"
      class="absolute left-0 top-full mt-2 text-xs text-n-slate-12 !bg-n-background !outline-none !shadow-none border-b border-n-weak !border-t-0 !border-l-0 !border-r-0 !rounded-none ring-0 focus:ring-0 w-48 appearance-none !p-0 z-10"
      @keydown="onKeydown"
      @blur="saveLink"
    />
  </div>
</template>
